import {io, Socket} from 'socket.io-client';
import type {Store} from '@reduxjs/toolkit';
import {addNotification} from '../store/slices/uiSlice';
import config from '../config';
import {DRIVE_API_TOKEN_STORAGE_KEY,getDriveApiToken} from '../lib/auth-token';

enum ConnectionState {DISCONNECTED='disconnected',CONNECTING='connecting',CONNECTED='connected',RECONNECTING='reconnecting',FAILED='failed'}

/** Use the authenticated Socket.IO protocol actually mounted by server.js.
 * Socket.IO owns heartbeat/backoff; do not create a second raw-WebSocket retry loop. */
class WebSocketService {
  private socket:Socket|null=null;
  private store:Store|null=null;
  private initialized=false;
  private authToken:string|null=null;
  private state=ConnectionState.DISCONNECTED;
  private warned=false;
  private handlers=new Map<string,Set<Function>>();
  initialize(store:Store){
    this.store=store;
    if(this.initialized)return;
    this.initialized=true;
    window.addEventListener('storage',event=>{if(event.key===DRIVE_API_TOKEN_STORAGE_KEY||event.key==='token')this.ensureConnected();});
    window.addEventListener('online',()=>this.reconnect());
    window.addEventListener('beforeunload',()=>this.disconnect());
    this.ensureConnected();
  }
  private connect(){
    if(!this.authToken||this.socket?.connected||this.state===ConnectionState.CONNECTING||this.state===ConnectionState.RECONNECTING||this.state===ConnectionState.FAILED)return;
    this.state=ConnectionState.CONNECTING;
    const endpoint=config.server.wsUrl.replace(/^ws:/,'http:').replace(/^wss:/,'https:');
    const socket=io(endpoint,{path:'/socket.io',auth:{token:this.authToken},transports:['websocket','polling'],reconnectionAttempts:5,reconnectionDelay:1000,reconnectionDelayMax:10000,autoConnect:false});
    this.socket=socket;
    socket.on('connect',()=>{this.state=ConnectionState.CONNECTED;this.warned=false;});
    socket.on('disconnect',reason=>{this.state=reason==='io client disconnect'?ConnectionState.DISCONNECTED:reason==='io server disconnect'?ConnectionState.FAILED:ConnectionState.RECONNECTING;});
    socket.on('connect_error',error=>{
      if(error.message==='APPLICATION_ACCESS_REQUIRED'){
        socket.disconnect();this.state=ConnectionState.FAILED;
        if(!this.warned){this.warned=true;this.store?.dispatch(addNotification({message:'Real-time access was denied. Sign in again to restore updates.',type:'warning',duration:6000}));}
      }
    });
    socket.io.on('reconnect_attempt',()=>{this.state=ConnectionState.RECONNECTING;});
    socket.io.on('reconnect_failed',()=>{
      this.state=ConnectionState.FAILED;
      if(!this.warned){this.warned=true;this.store?.dispatch(addNotification({message:'Real-time connection unavailable. Reconnect when your network recovers.',type:'warning',duration:6000}));}
    });
    socket.onAny((event:string,data:any)=>{for(const handler of this.handlers.get(event.toLowerCase())||[])handler(data);});
    socket.connect();
  }
  // Folder notifications require server-side ACL-aware subscriptions. Never join arbitrary resource rooms.
  setCurrentFolder(_folderId:string|null){}
  ensureConnected(){
    const token=getDriveApiToken();
    if(token!==this.authToken){this.disconnect();this.authToken=token;this.warned=false;}
    if(token)this.connect();
  }
  reconnect(){this.disconnect();this.authToken=getDriveApiToken();this.warned=false;this.connect();}
  disconnect(){this.socket?.removeAllListeners();this.socket?.io.removeAllListeners();this.socket?.disconnect();this.socket=null;this.state=ConnectionState.DISCONNECTED;}
  subscribe(event:string,handler:Function){const key=event.toLowerCase();if(!this.handlers.has(key))this.handlers.set(key,new Set());this.handlers.get(key)!.add(handler);return()=>this.unsubscribe(event,handler);}
  unsubscribe(event:string,handler:Function){this.handlers.get(event.toLowerCase())?.delete(handler);}
  isConnected(){return this.socket?.connected===true;}
  getConnectionState(){return this.state;}
}
export default new WebSocketService();
